import React, { useEffect, useMemo, useState } from 'react'
import { useToast } from '@chakra-ui/react'
import { v4 as uuidv4 } from 'uuid'
import { getCurrentUser } from 'api'
import { getToken, clearToken } from 'lib/localStorage'
import { Outlet, useNavigate } from 'react-router-dom'
import { AppContextProvider } from 'hooks/useAppContext'
import { Layout } from 'components'

export const AuthGuard = () => {
  const navigate = useNavigate()
  const token = getToken()
  const [currentUser, setCurrentUser] = useState(null)
  const [isError, setIsError] = useState(false)

  const toastFunc = useToast()
  const [toast, setToast] = useState({
    id: 0,
    title: '',
    description: '',
    status: ''
  })

  const createToast = (status = 'success', title = '', description = '') => {
    setToast({
      id: uuidv4(),
      title,
      description: typeof description === 'string' ? description : JSON.stringify(description),
      status
    })
  }

  useEffect(() => {
    if (token.value) {
      // Load current logged in user data
      const load = async () => {
        try {
          const user = await getCurrentUser()
          user.name = user.firstname + ' ' + user.lastname
          setCurrentUser(user)
        } catch (err) {
          clearToken()
          navigate('/login')
        }
      }
      load()

      // Check token expiration every 1 minute
      const checkTokenExpiration = () => {
        const currentTime = Math.floor(Date.now() / 1000) // Convert to seconds
        if (token.exp < currentTime) {
          clearToken()
          navigate('/login')
        }
      }

      checkTokenExpiration()
      const checkInterval = setInterval(checkTokenExpiration, 60000) // Check every minute

      return () => {
        clearInterval(checkInterval)
      }
    } else {
      navigate('/login')
    }
  }, [token.value, token.exp])

  useEffect(() => {
    if (toast.title || toast.description) {
      toastFunc({
        title: toast.title,
        description: toast.description,
        status: toast.status,
        position: 'top',
        duration: 3000,
        isClosable: true
      })
    }
  }, [toast.id])

  const contextValue = useMemo(() => ({
    currentUser,
    setCurrentUser,
    createToast,
    isError,
    setIsError
  }), [currentUser])

  if (isError) {
    throw new Error('Oops! Something went wrong.')
  }

  return (
    <AppContextProvider value={contextValue}>
      <Layout>
        <Outlet />
      </Layout>
    </AppContextProvider>
  )
}
